import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  computeLivestreamDetailSummaryFromStreams,
  formatLivestreamDurationMinutes,
  formatLivestreamRankingViewsCount,
  formatViewerIntegerFloor,
  LIVESTREAM_METRIC_LABELS,
  RIGHT_TABLE_SORT_LABEL_SX,
  SortDirection,
  type LivestreamDetailSummaryStream,
} from "./utils";
export const CHANNEL_UNASSIGNED_PROGRAM_SLUG = "none";

export type ChannelProgramTableStream = LivestreamDetailSummaryStream & {
  livestreamProgramId?: string | null;
  programName?: string | null;
  viewIncrement?: number | null;
  streamTotalViews?: number | null;
};

type ProgramRow = {
  programId: string;
  name: string;
  livestreams: number;
  views: number;
  viewsAvg: number;
  avgViewers: number;
  avgPeak: number;
  hoursMinutes: number;
};

type SortColumn =
  | "program"
  | "livestreams"
  | "views"
  | "viewsAvg"
  | "avgViewers"
  | "avgPeak"
  | "hours";

function videoKey(row: ChannelProgramTableStream): string {
  return row.livestreamId?.trim() || row._id || "";
}

function programKey(row: ChannelProgramTableStream): string {
  return row.livestreamProgramId?.trim() || CHANNEL_UNASSIGNED_PROGRAM_SLUG;
}

/** Same views the channel livestream row shows for this video. */
function viewsShownOnChannelRow(row: ChannelProgramTableStream): number {
  const streamViews = Number(row.streamTotalViews) || 0;
  if (streamViews > 0) return streamViews;
  return Math.max(0, Number(row.totalViews) || 0);
}

function sliceViewCount(row: ChannelProgramTableStream): number {
  const increment = Math.max(0, Number(row.viewIncrement) || 0);
  if (increment > 0) return increment;
  return Math.max(0, Number(row.totalViews) || 0);
}

function videosSharedByPrograms(streams: ChannelProgramTableStream[]): Set<string> {
  const programs = new Map<string, Set<string>>();
  for (const row of streams) {
    const key = videoKey(row);
    if (!key) continue;
    const ids = programs.get(key) ?? new Set<string>();
    ids.add(programKey(row));
    programs.set(key, ids);
  }
  const shared = new Set<string>();
  programs.forEach((ids, key) => {
    if (ids.size > 1) shared.add(key);
  });
  return shared;
}

/**
 * Views for one program. A video that is only this show uses the YouTube views
 * on the channel row. A video shared by several shows uses each slice's increment.
 */
export function programViewsFromStreams(
  rows: ChannelProgramTableStream[],
  sharedVideos: Set<string>,
): number {
  const byVideo = new Map<string, ChannelProgramTableStream[]>();
  for (const row of rows) {
    const key = videoKey(row) || `row:${byVideo.size}`;
    const list = byVideo.get(key);
    if (list) list.push(row);
    else byVideo.set(key, [row]);
  }
  let sum = 0;
  byVideo.forEach((slices, key) => {
    if (sharedVideos.has(key)) {
      sum += slices.reduce((total, row) => total + sliceViewCount(row), 0);
      return;
    }
    const shown = slices.reduce(
      (highest, row) => Math.max(highest, viewsShownOnChannelRow(row)),
      0,
    );
    sum +=
      shown > 0
        ? shown
        : slices.reduce((total, row) => total + sliceViewCount(row), 0);
  });
  return sum;
}

const TABLE_MIN_WIDTH = 720;
const RANK_COL_SX = {
  width: 36,
  minWidth: 36,
  maxWidth: 36,
  px: 0.5,
} as const;

function buildProgramRows(streams: ChannelProgramTableStream[]): ProgramRow[] {
  const buckets = new Map<string, ChannelProgramTableStream[]>();
  const sharedVideos = videosSharedByPrograms(streams);
  for (const stream of streams) {
    const id = programKey(stream);
    const existing = buckets.get(id);
    if (existing) existing.push(stream);
    else buckets.set(id, [stream]);
  }

  return Array.from(buckets.entries()).map(([programId, rows]) => {
    const summary = computeLivestreamDetailSummaryFromStreams(rows);
    const named = rows.find((s) => s.programName?.trim())?.programName?.trim();
    const views = programViewsFromStreams(rows, sharedVideos);
    return {
      programId,
      name: named || "",
      livestreams: summary.episodeCount,
      views,
      viewsAvg: rows.length ? Math.round(views / rows.length) : 0,
      avgViewers: summary.avgViewers,
      avgPeak: summary.avgPeakViewers,
      hoursMinutes: summary.totalDurationMinutes,
    };
  });
}

function compareProgramRows(
  a: ProgramRow,
  b: ProgramRow,
  column: SortColumn,
  direction: SortDirection,
  unassignedLabel: string,
  programFallback: string,
): number {
  const dir = direction === "asc" ? 1 : -1;
  const label = (row: ProgramRow) =>
    row.programId === CHANNEL_UNASSIGNED_PROGRAM_SLUG
      ? unassignedLabel
      : row.name || programFallback;
  let cmp = 0;
  switch (column) {
    case "program":
      cmp = label(a).localeCompare(label(b), "es", { sensitivity: "base" });
      break;
    case "livestreams":
      cmp = a.livestreams - b.livestreams;
      break;
    case "views":
      cmp = a.views - b.views;
      break;
    case "viewsAvg":
      cmp = a.viewsAvg - b.viewsAvg;
      break;
    case "avgViewers":
      cmp = a.avgViewers - b.avgViewers;
      break;
    case "avgPeak":
      cmp = a.avgPeak - b.avgPeak;
      break;
    case "hours":
      cmp = a.hoursMinutes - b.hoursMinutes;
      break;
    default:
      cmp = 0;
  }
  return cmp * dir;
}

type ChannelProgramsTableProps = {
  livestreams: ChannelProgramTableStream[];
  isLoading?: boolean;
  onRowClick: (programId: string) => void;
};

export const ChannelProgramsTable: React.FC<ChannelProgramsTableProps> = ({
  livestreams,
  isLoading = false,
  onRowClick,
}) => {
  const { t } = useTranslation("explorer");
  const [sortColumn, setSortColumn] = useState<SortColumn>("views");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const unassignedLabel = t("noProgram");
  const programFallback = t("program");

  const rows = useMemo(() => {
    const built = buildProgramRows(livestreams);
    return [...built].sort((a, b) =>
      compareProgramRows(
        a,
        b,
        sortColumn,
        sortDirection,
        unassignedLabel,
        programFallback,
      ),
    );
  }, [livestreams, sortColumn, sortDirection, unassignedLabel, programFallback]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortColumn(column);
    setSortDirection(column === "program" ? "asc" : "desc");
  };

  const rowLabel = (row: ProgramRow) =>
    row.programId === CHANNEL_UNASSIGNED_PROGRAM_SLUG
      ? unassignedLabel
      : row.name || programFallback;

  if (!isLoading && livestreams.length === 0) return null;

  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        {isLoading ? (
          <Skeleton variant="text" width={160} height={36} />
        ) : (
          <Typography variant="h2" color="text.primary">
            {t("programs")}
          </Typography>
        )}
      </Stack>

      <TableContainer component={Paper} sx={{ overflowX: "auto", width: "100%" }}>
        <Table sx={{ width: "100%", minWidth: { xs: TABLE_MIN_WIDTH, md: "100%" } }}>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={RANK_COL_SX}>
                #
              </TableCell>
              <TableCell
                sortDirection={sortColumn === "program" ? sortDirection : false}
              >
                <TableSortLabel
                  active={sortColumn === "program"}
                  direction={sortColumn === "program" ? sortDirection : "asc"}
                  onClick={() => handleSort("program")}
                >
                  <Tooltip title={t("tooltip.linkedProgram")} arrow placement="top">
                    <Box component="span" sx={{ display: "inline-flex" }}>
                      {t("program")}
                    </Box>
                  </Tooltip>
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="right"
                sortDirection={sortColumn === "livestreams" ? sortDirection : false}
              >
                <TableSortLabel
                  active={sortColumn === "livestreams"}
                  direction={
                    sortColumn === "livestreams" ? sortDirection : "desc"
                  }
                  onClick={() => handleSort("livestreams")}
                  sx={RIGHT_TABLE_SORT_LABEL_SX}
                >
                  <Tooltip
                    title={t("tooltip.livestreamCount")}
                    arrow
                    placement="top"
                  >
                    <Box component="span" sx={{ display: "inline-flex" }}>
                      {t("livestreams")}
                    </Box>
                  </Tooltip>
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="right"
                sortDirection={sortColumn === "views" ? sortDirection : false}
              >
                <TableSortLabel
                  active={sortColumn === "views"}
                  direction={sortColumn === "views" ? sortDirection : "desc"}
                  onClick={() => handleSort("views")}
                  sx={RIGHT_TABLE_SORT_LABEL_SX}
                >
                  <Tooltip
                    title={t("tooltip.contentViewsPeriod")}
                    arrow
                    placement="top"
                  >
                    <Box component="span" sx={{ display: "inline-flex" }}>
                      {t("views")}
                    </Box>
                  </Tooltip>
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="right"
                sortDirection={sortColumn === "viewsAvg" ? sortDirection : false}
              >
                <TableSortLabel
                  active={sortColumn === "viewsAvg"}
                  direction={sortColumn === "viewsAvg" ? sortDirection : "desc"}
                  onClick={() => handleSort("viewsAvg")}
                  sx={RIGHT_TABLE_SORT_LABEL_SX}
                >
                  <Tooltip
                    title={t("tooltip.viewsPerEpisode")}
                    arrow
                    placement="top"
                  >
                    <Box component="span" sx={{ display: "inline-flex" }}>
                      {t("viewsAvgShort")}
                    </Box>
                  </Tooltip>
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="right"
                sortDirection={sortColumn === "avgViewers" ? sortDirection : false}
              >
                <TableSortLabel
                  active={sortColumn === "avgViewers"}
                  direction={
                    sortColumn === "avgViewers" ? sortDirection : "desc"
                  }
                  onClick={() => handleSort("avgViewers")}
                  sx={RIGHT_TABLE_SORT_LABEL_SX}
                >
                  <Tooltip
                    title={t("tooltip.avgConcurrentAll")}
                    arrow
                    placement="top"
                  >
                    <Box component="span" sx={{ display: "inline-flex" }}>
                      {LIVESTREAM_METRIC_LABELS.avgEsp}
                    </Box>
                  </Tooltip>
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="right"
                sortDirection={sortColumn === "avgPeak" ? sortDirection : false}
              >
                <TableSortLabel
                  active={sortColumn === "avgPeak"}
                  direction={sortColumn === "avgPeak" ? sortDirection : "desc"}
                  onClick={() => handleSort("avgPeak")}
                  sx={RIGHT_TABLE_SORT_LABEL_SX}
                >
                  <Tooltip title={t("tooltip.avgPeakEach")} arrow placement="top">
                    <Box component="span" sx={{ display: "inline-flex" }}>
                      {LIVESTREAM_METRIC_LABELS.avgPeakEsp}
                    </Box>
                  </Tooltip>
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="right"
                sortDirection={sortColumn === "hours" ? sortDirection : false}
              >
                <TableSortLabel
                  active={sortColumn === "hours"}
                  direction={sortColumn === "hours" ? sortDirection : "desc"}
                  onClick={() => handleSort("hours")}
                  sx={RIGHT_TABLE_SORT_LABEL_SX}
                >
                  <Tooltip
                    title={t("tooltip.totalLivestreamDuration")}
                    arrow
                    placement="top"
                  >
                    <Box component="span" sx={{ display: "inline-flex" }}>
                      {t("hours")}
                    </Box>
                  </Tooltip>
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from({ length: 5 }, (_, i) => (
                  <TableRow key={`channel-program-skeleton-${i}`}>
                    <TableCell align="center" sx={RANK_COL_SX}>
                      <Skeleton variant="text" width={16} height={22} sx={{ mx: "auto" }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width="70%" height={22} />
                    </TableCell>
                    {Array.from({ length: 6 }, (_, j) => (
                      <TableCell key={j} align="right">
                        <Skeleton
                          variant="text"
                          width={48}
                          height={22}
                          sx={{ ml: "auto" }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : rows.map((row, index) => (
                  <TableRow
                    key={row.programId}
                    hover
                    onClick={() => {
                      if (window.getSelection()?.toString().trim()) return;
                      onRowClick(row.programId);
                    }}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell align="center" sx={RANK_COL_SX}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        color="text.secondary"
                      >
                        {index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {rowLabel(row)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{row.livestreams}</TableCell>
                    <TableCell align="right">
                      {formatLivestreamRankingViewsCount(row.views)}
                    </TableCell>
                    <TableCell align="right">
                      {formatLivestreamRankingViewsCount(row.viewsAvg)}
                    </TableCell>
                    <TableCell align="right">
                      {formatViewerIntegerFloor(row.avgViewers)}
                    </TableCell>
                    <TableCell align="right">
                      {formatViewerIntegerFloor(row.avgPeak)}
                    </TableCell>
                    <TableCell align="right">
                      {formatLivestreamDurationMinutes(row.hoursMinutes)}
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ChannelProgramsTable;
